import React, { useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { Order } from 'core/domains/order';
import OverviewBox from 'components/overview-box/overview-box';
import './order-piechart-channel.scss';
// Register all components
Chart.register(...registerables);


// Define the props for the component
interface OrderPieChartProps {
    orders: Order[]; // Array of orders
}

const OrderPieChartByChannels: React.FC<OrderPieChartProps> = ({ orders }) => {
    const chartRef = React.useRef<Chart | null>(null); // Reference for the chart instance

    useEffect(() => {
        // Count the occurrences of each order type
        const orderCounts = orders.reduce<Record<string, number>>((acc, order: Order) => {
            acc[order.referral] = (acc[order.referral] || 0) + 1;
            return acc;
        }, {});

        // Prepare the data for the pie chart
        const data = {
            labels: Object.keys(orderCounts),
            datasets: [{
                data: Object.values(orderCounts),
                backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(255, 159, 64, 0.6)', // Orange
                    'rgba(255, 205, 86, 0.6)', // Yellow
                    'rgba(75, 192, 192, 0.6)', // Teal
                    'rgba(54, 162, 235, 0.6)', // Light Blue
                    'rgba(153, 102, 255, 0.6)', // Purple
                    'rgba(201, 203, 207, 0.6)', // Grey
                    'rgba(255, 99, 71, 0.6)', // Tomato
                    'rgba(255, 215, 0, 0.6)', // Gold
                    'rgba(0, 128, 0, 0.6)', // Green
                    'rgba(0, 0, 255, 0.6)', // Blue
                    'rgba(128, 0, 128, 0.6)', // Purple
                    'rgba(0, 255, 255, 0.6)', // Cyan
                    'rgba(255, 20, 147, 0.6)', // Deep Pink
                    'rgba(255, 105, 180, 0.6)', // Hot Pink
                    'rgba(240, 230, 140, 0.6)', // Khaki
                    'rgba(124, 252, 0, 0.6)', // Lawn Green
                    'rgba(255, 228, 196, 0.6)', // Bisque
                    'rgba(0, 255, 127, 0.6)', // Spring Green
                    'rgba(135, 206, 250, 0.6)', // Light Sky Blue
                    'rgba(255, 140, 0, 0.6)', // Dark Orange
                    'rgba(0, 191, 255, 0.6)', // Deep Sky Blue
                    'rgba(255, 182, 193, 0.6)', // Light Pink
                    'rgba(255, 69, 0, 0.6)', // Red Orange
                    'rgba(255, 228, 181, 0.6)', // Papaya Whip
                    'rgba(173, 216, 230, 0.6)'],
                hoverBackgroundColor: [
                    'rgba(255, 99, 132, 0.8)', // Red
                    'rgba(255, 159, 64, 0.8)', // Orange
                    'rgba(255, 205, 86, 0.8)', // Yellow
                    'rgba(75, 192, 192, 0.8)', // Teal
                    'rgba(54, 162, 235, 0.8)', // Light Blue
                    'rgba(153, 102, 255, 0.8)', // Purple
                    'rgba(201, 203, 207, 0.8)', // Grey
                    'rgba(255, 99, 71, 0.8)', // Tomato
                    'rgba(255, 215, 0, 0.8)', // Gold
                    'rgba(0, 128, 0, 0.8)', // Green
                    'rgba(0, 0, 255, 0.8)', // Blue
                    'rgba(128, 0, 128, 0.8)', // Purple
                    'rgba(0, 255, 255, 0.8)', // Cyan
                    'rgba(255, 20, 147, 0.8)', // Deep Pink
                    'rgba(255, 105, 180, 0.8)', // Hot Pink
                    'rgba(240, 230, 140, 0.8)', // Khaki
                    'rgba(124, 252, 0, 0.8)', // Lawn Green
                    'rgba(255, 228, 196, 0.8)', // Bisque
                    'rgba(0, 255, 127, 0.8)', // Spring Green
                    'rgba(135, 206, 250, 0.8)', // Light Sky Blue
                    'rgba(255, 140, 0, 0.8)', // Dark Orange
                    'rgba(0, 191, 255, 0.8)', // Deep Sky Blue
                    'rgba(255, 182, 193, 0.8)', // Light Pink
                    'rgba(255, 69, 0, 0.8)', // Red Orange
                    'rgba(255, 228, 181, 0.8)', // Papaya Whip
                    'rgba(173, 216, 230, 0.8)', // Light Blue
                ]
            }]
        };
        // Configuration for the pie chart
        const config = {
            type: 'doughnut',
            data: data,
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            font: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 14, // Set font size for legend
                            },
                        },
                    },
                    tooltip: {
                        titleFont: {
                            family: 'YekanBakh', // Set font family for legend
                            size: 14, // Set font size for tooltip title
                        },
                        bodyFont: {
                            family: 'YekanBakh', // Set font family for legend
                            size: 12, // Set font size for tooltip body
                        },
                        callbacks: {
                            label: function (tooltipItem: any) {
                                const label = tooltipItem.label || '';
                                const value = tooltipItem.raw || 0;
                                return `${label}: ${value} سفارش`;
                            }
                        }
                    }
                }
            }
        };
        // Get the context of the canvas
        const ctx = document.getElementById('orderPieChartByChannels') as HTMLCanvasElement;

        // Destroy the previous chart instance if it exists
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        // Create a new chart and store the reference
        chartRef.current = new Chart(ctx.getContext('2d') as CanvasRenderingContext2D, config as any);

        // Cleanup function to destroy the chart on component unmount
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [orders]);

    const getTotalValueOfNewOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.orderType.id == 0).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;

    const getTotalValueOfExtendOrders = orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.orderType.id == 1).reduce((accumulator, order) => {
        return accumulator + order.amount;
    }, 0) : 0;

    return (
        <div className='overview-order-piechart-channels'>
            <canvas id="orderPieChartByChannels" width="200" height="200"></canvas>
            {/* <div className="order-types-info">
                <OverviewBox
                    hideCount
                    title={'سفارش جدید'}
                    value={getTotalValueOfNewOrders}
                    count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.orderType.id == 0).length : 0}
                />
                <OverviewBox
                    hideCount
                    title={'تمدید'}
                    value={getTotalValueOfExtendOrders}
                    count={orders.length > 0 ? orders.filter(s => s.state.id == 2 && s.orderType.id == 1).length : 0}
                />
            </div> */}
        </div>
    );
};


export default OrderPieChartByChannels;
